<template>
  <div :class="[...containerClasses, $attrs.class]" :style="containerCssVars">
    <slot />
  </div>
</template>

<script lang="ts" setup>
const BASE_CLASS = 'n-layout-row';

interface Props {
  background?: {
    color?: string;
    mobileImage?: string;
    mobileImageSize?: string;
    desktopImage?: string;
    desktopImageSize?: string;
  };
  cssFlags?: string[];
  hideOnBreakpoint?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  cssFlags: () => [],
  hideOnBreakpoint: () => []
});

defineOptions({
  inheritAttrs: false
});
const containerCssVars = computed(() => {
  const cssVars = {
    '--row-background': (props.background?.color ?? 'transparent') || 'transparent'
  };

  if (String(props.background?.mobileImage ?? '').length) {
    cssVars['--background-image-mobile'] = `url(${props.background?.mobileImage})`;
    cssVars['--background-image-mobile-size'] = props.background?.mobileImageSize || 'cover';
  }

  if (String(props.background?.desktopImage ?? '').length) {
    cssVars['--background-image-desktop'] = `url(${props.background?.desktopImage})`;
    cssVars['--background-image-desktop-size'] = props.background?.desktopImageSize || 'cover';
  }
  return cssVars;
});
const containerClasses = computed(() => {
  const classes = [
    BASE_CLASS,
    `${BASE_CLASS}--hide-on`,
    ...props.cssFlags.map((f) => `${BASE_CLASS}--${f}`),
    ...props.hideOnBreakpoint.map((f) => `${BASE_CLASS}--hide-on--${f}`)
  ];
  if (String(props.background?.mobileImage ?? '').length) {
    classes.push(`${BASE_CLASS}--background-image-mobile`);
  }
  if (String(props.background?.desktopImage ?? '').length) {
    classes.push(`${BASE_CLASS}--background-image-desktop`);
  }
  return classes;
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.n-layout-row {
  background-color: var(--row-background);
  padding: 40px 0;
  position: relative;

  @include bp(lg) {
    padding: 80px 0;
  }

  &--no-top-padding {
    padding-top: 0;

    @include bp(lg) {
      padding-top: 0;
    }
  }
  &--no-bottom-padding {
    padding-bottom: 0;

    @include bp(lg) {
      padding-bottom: 0;
    }
  }

  &--overflow-hidden {
    overflow: hidden;
  }

  &--shorter-background {
    background-color: transparent;
    &::before {
      position: absolute;
      content: '';
      top: 0;
      left: 0;
      width: 100%;
      height: 85%;
      background-color: var(--row-background);
    }
  }

  &--background-image-mobile {
    background-image: var(--background-image-mobile);
    background-size: var(--background-image-mobile-size);
    background-repeat: no-repeat;
    background-position: center;
  }

  &--background-image-desktop {
    @include bp(md) {
      background-image: var(--background-image-desktop);
      background-size: var(--background-image-desktop-size);
      background-repeat: no-repeat;
      background-position: center;
    }
  }

  &--hide-on {
    $breakpoints: xs, sm, md, lg;

    @each $breakpoint in $breakpoints {
      &--#{$breakpoint} {
        @include bp($breakpoint) {
          display: none;
        }
      }
    }
  }
}
</style>
